import React, { useEffect, useMemo, useState, forwardRef } from 'react';
import styles from './textarea.module.scss';
import { TextareaProps } from '../../atoms.interfaces';

const ENV = (import.meta as any).env;
const IS_WEB = ENV?.VITE_APP_IS_WEB === 'true';
const IS_SAFARI = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
const IS_WEB_SAFARI = IS_WEB && IS_SAFARI;

const TYPOGRAPHY = {
  heading4: IS_WEB_SAFARI ? 'heading4Safari' : 'heading4',
  heading4L: IS_WEB_SAFARI ? 'heading4Safari' : 'heading4L',
  heading5: 'heading5',
  detail: 'detail',
  paragraph: 'paragraph',
} as const;

const TEXTAREA_VARIANTS = {
  default: 'default',
  containedLabel: 'containedLabel',
} as const;

type TextareaVariant = keyof typeof TEXTAREA_VARIANTS;

interface ClassNameBuilderConfig {
  base: string;
  conditions: Array<{ condition: boolean; className: string }>;
  typography?: keyof typeof TYPOGRAPHY;
}

interface LabelClassConfig {
  variant: TextareaVariant;
  isActiveOrHasValue?: boolean;
  disabled?: boolean;
}

interface ContainerClassConfig {
  variant: TextareaVariant;
  styleVariant: string;
  border: boolean;
  disabled: boolean;
  color: string;
}

interface AreaClassConfig {
  variant: TextareaVariant;
  disabled: boolean;
}

const buildClassName = ({ base, conditions, typography }: ClassNameBuilderConfig): string => {
  const classes = [base, typography ? TYPOGRAPHY[typography] : '', ...conditions.filter(c => c.condition).map(c => c.className)];
  return classes.filter(Boolean).join(' ');
};

interface UpdatedTextareaProps extends Omit<TextareaProps, 'variant'> {
  variant?: TextareaVariant;
  styleVariant?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, UpdatedTextareaProps>(
  (
    {
      variant = 'default',
      placeholder,
      label,
      onChange,
      description,
      onDescriptionClick,
      styleVariant = 'primary',
      extraDescription,
      border = false,
      disabled = false,
      additionalStyles = null,
      color = 'grey',
      rows = 3,
      ...props
    }: UpdatedTextareaProps,
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const [isActiveOrHasValue, setActiveOrHasValue] = useState(Boolean(props?.value));

    const isContainedLabel = variant === 'containedLabel';
    const isDefaultVariant = variant === 'default';

    const classNames = useMemo(() => {
      const buildLabelClasses = (config: LabelClassConfig): string => {
        const isActive = Boolean(config.isActiveOrHasValue);
        if (config.variant === 'containedLabel') {
          return buildClassName({
            base: styles.Textarea__Label,
            typography: isActive ? 'detail' : 'paragraph',
            conditions: [
              { condition: isActive, className: styles.activeLabel },
              { condition: true, className: styles.Textarea__Label__Contained },
              { condition: config.disabled || false, className: styles.Textarea__Label__Contained__Disabled },
            ],
          });
        }
        return buildClassName({ base: styles.Textarea__Label, typography: isActive ? 'heading4' : 'heading4', conditions: [] });
      };

      const buildContainerClasses = (config: ContainerClassConfig): string => {
        return buildClassName({
          base: styles.Textarea__Container,
          typography: 'heading5',
          conditions: [
            { condition: !(config.disabled && config.variant === 'default'), className: styles[config.styleVariant] },
            { condition: config.border, className: styles.border },
            { condition: config.variant === 'containedLabel', className: styles.containedLabel },
            { condition: config.disabled && config.variant === 'containedLabel', className: styles.containedLabel__Disabled },
            { condition: config.disabled && config.variant === 'default', className: styles.Textarea__Container__Disabled },
            { condition: config.color === 'grey' && !(config.disabled && config.variant === 'default'), className: styles.greyBackground },
          ],
        });
      };

      const buildAreaClasses = (config: AreaClassConfig): string => {
        return buildClassName({
          base: styles.Textarea__Area,
          conditions: [
            { condition: IS_WEB, className: styles.Textarea__Area__Web },
            { condition: config.variant === 'containedLabel', className: styles.Textarea__Area__Contained },
            { condition: config.disabled, className: styles.Textarea__Area__Contained__Disabled },
          ],
        });
      };

      return {
        container: buildContainerClasses({ variant, styleVariant, border, disabled, color }),
        area: buildAreaClasses({ variant, disabled }),
        label: buildLabelClasses({ variant, isActiveOrHasValue, disabled }),
        standardLabel: buildLabelClasses({ variant: 'default', disabled }),
        description: buildClassName({
          base: styles.Textarea__Description,
          typography: 'paragraph',
          conditions: [
            { condition: true, className: styles[`Textarea__Description__${styleVariant}`] },
            { condition: Boolean(onDescriptionClick), className: styles.Textarea__Description__Clickable },
          ],
        }),
      };
    }, [variant, styleVariant, border, disabled, color, isActiveOrHasValue, onDescriptionClick]);

    const handleBlur = () => {
      setIsFocused(false);
      if (!props.value) setActiveOrHasValue(false);
    };

    const handleFocus = () => {
      setIsFocused(true);
      setActiveOrHasValue(true);
    };

    useEffect(() => {
      setActiveOrHasValue(Boolean(props.value) || isFocused);
    }, [props.value, isFocused]);

    const { className: userClassName, ...restProps } = props as any;

    const mergedAreaClass = [classNames.area, TYPOGRAPHY.heading4, userClassName]
      .filter(Boolean)
      .join(' ');

    const sanitizedAreaClass = Array.from(new Set(mergedAreaClass.split(/\s+/)))
      .filter((token) => token && token !== 'paragraph')
      .join(' ');

    const areaProps = {
      // key: props.name,
      ref,
      placeholder: isDefaultVariant ? placeholder : '',
      onChange,
      disabled,
      rows,
      ...restProps,
      className: sanitizedAreaClass,
    };

    return (
      <div className={styles.Textarea} style={additionalStyles || {}} onBlur={handleBlur} onFocus={handleFocus}>
        {label && isDefaultVariant && <span className={classNames.standardLabel}>{label}</span>}

        {isDefaultVariant && (description || extraDescription) && (
          <div className={styles.Textarea__Description__Container__UnderLabel}>
            {description && (
              <span className={classNames.description} onClick={onDescriptionClick}>
                {description}
              </span>
            )}
            {extraDescription && (
              <span className={`${TYPOGRAPHY.detail} ${styles.Textarea__ExtraDescription}`}>{extraDescription}</span>
            )}
          </div>
        )}

        <div className={classNames.container}>
          {label && isContainedLabel && <div className={classNames.label}>{label}</div>}
          <textarea {...areaProps} />
        </div>

        {isContainedLabel && (description || extraDescription) && (
          <div className={styles.Textarea__Description__Container}>
            {description && (
              <span className={classNames.description} onClick={onDescriptionClick}>
                {description}
              </span>
            )}
            {extraDescription && (
              <span className={`${TYPOGRAPHY.detail} ${styles.Textarea__ExtraDescription}`}>{extraDescription}</span>
            )}
          </div>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

export default Textarea;

